import PropTypes from "prop-types"
import React, { useState, useContext, useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import i18n from "i18n"
import { API, Auth } from "aws-amplify"
import Intercom from "@intercom/messenger-js-sdk"

import "react-perfect-scrollbar/dist/css/styles.css"
import { Box, List, Tooltip } from "@mui/material"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import DashboardIcon from "@mui/icons-material/DashboardOutlined"
import FolderIcon from "@mui/icons-material/FolderOutlined"
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboardOutlined"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFileOutlined"
import ReceiptIcon from "@mui/icons-material/ReceiptOutlined"
import PaidIcon from "@mui/icons-material/PaidOutlined"
import PeopleIcon from "@mui/icons-material/PeopleOutlined"
import SlideshowIcon from "@mui/icons-material/SlideshowOutlined"
import NotificationsIcon from "@mui/icons-material/NotificationsOutlined"
import PersonIcon from "@mui/icons-material/PersonOutline"
import HelpIcon from "@mui/icons-material/HelpOutline"
import DiamondIcon from "@mui/icons-material/DiamondOutlined"

import SidebarLogo from "./SidebarLogo"
import SidebarCentralNavButton from "./SidebarCentralNavButton"
import SidebarBottomNavButton from "./SidebarBottomNavButton"
import AuthContext from "context/auth/authContext"
import { useSubscriptionContext } from "context/subscription/subscriptionContext"
import FeaturebaseWidget from "common/FeaturebaseWidget"
import { getLayoutConfig } from "./layoutConfig"
import "./Sidebar.css"

const navItems = [
  {
    icon: <DashboardIcon />,
    label: i18n.t("sidebar.dashboard"),
    disabled: true,
    tooltip: i18n.t("sidebar.requestDemo"),
  },
  {
    icon: <FolderIcon />,
    label: i18n.t("sidebar.projects"),
    link: "/dashboard",
    dataElement: "sidebarProjects",
    activeRoutes: [
      "dashboard",
      "budgeting/",
      "certificaciones/",
      "certhistory/",
      "extras/",
      "dynamicElements",
      "expenses/",
      "invoices/",
      "overview/",
    ],
  },
  {
    icon: <SpaceDashboardIcon />,
    label: i18n.t("sidebar.libraries"),
    activeRoutes: ["bancodeprecios", "bancodeprecios/"],
    submenu: [
      {
        label: i18n.t("sidebar.myPriceBank"),
        link: "/bancodeprecios",
        dataElement: "sidebarMyPriceBank",
      },
      {
        label: i18n.t("sidebar.templates"),
        link: "/bancodeprecios/templates",
        dataElement: "sidebarPriceBankTemplates",
      },
      {
        label: i18n.t("sidebar.referenceBanks"),
        link: "/bancodeprecios/select",
        dataElement: "sidebarReferenceBanks",
      },
      {
        label: i18n.t("sidebar.dynamicElements"),
        link: "/bancodeprecios/dynamic-element",
        dataElement: "sidebarDynamicElements",
      },
    ],
  },
  {
    icon: <InsertDriveFileIcon />,
    label: i18n.t("sidebar.projectManagement"),
    submenu: [
      {
        label: i18n.t("sidebar.workMinutes"),
        link: "/actas-obra",
        disabled: true,
        rightIcon: <DiamondIcon />,
        tooltip: i18n.t("sidebar.requestDemo"),
      },
    ],
  },
  {
    icon: <ReceiptIcon />,
    label: i18n.t("sidebar.sales"),
    activeRoutes: ["invoices", "proforms"],
    submenu: [
      {
        label: i18n.t("sidebar.invoices"),
        link: "/invoices",
        dataElement: "sidebarInvoices",
      },
      {
        label: i18n.t("sidebar.proforms"),
        link: "/proforms",
        rightIcon: <DiamondIcon />,
        disabled: true,
        tooltip: i18n.t("sidebar.requestDemo"),
      },
    ],
  },
  {
    icon: <PaidIcon />,
    label: i18n.t("sidebar.purchases"),
    activeRoutes: ["expenses"],
    submenu: [
      {
        label: i18n.t("sidebar.costControl"),
        link: "/expenses",
        dataElement: "sidebarExpenses",
      },
      {
        label: i18n.t("sidebar.purchaseOrders"),
        link: "/purchase-orders",
        dataElement: "sidebarPurchaseOrders",
      },
    ],
  },
  {
    icon: <PeopleIcon />,
    label: i18n.t("sidebar.contacts"),
    link: "/contacts",
    activeRoutes: ["contacts"],
    dataElement: "sidebarContacts",
  },
]

const Sidebar = props => {
  const location = useLocation()
  const { showSidebar } = getLayoutConfig(location.pathname)

  if (!showSidebar) return null

  const { user, logOut } = useContext(AuthContext)

  const intercomInitialized = useRef(false)

  useEffect(() => {
    let isMounted = true

    const initializeIntercom = async () => {
      if (!user || intercomInitialized.current) return

      try {
        const jwtToken = (await Auth.currentSession())
          .getIdToken()
          .getJwtToken()
        const res = await API.post("CORE", "/intercom/", {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "POST",
            "Access-Control-Allow-Credentials": "true",
          },
        })

        if (!isMounted) return

        Intercom({
          app_id: "l7ga9htz",
          intercom_user_jwt: res,
          z_index: 1200,
        })

        intercomInitialized.current = true
      } catch (error) {
        console.error("Intercom initialization failed:", error)
      }
    }

    initializeIntercom()

    return () => {
      isMounted = false
    }
  }, [user])

  const { subscriptionState, isTrial, noSubscription } =
    useSubscriptionContext()

  const [collapsed, setCollapsed] = useState(true)
  const [openSubmenu, setOpenSubmenu] = useState(null)
  const sidebarRef = useRef(null)
  const hoverTimerRef = useRef(null)
  const autoExpandedRef = useRef(false)

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }

  const handleMouseEnter = () => {
    if (collapsed && !hoverTimerRef.current) {
      hoverTimerRef.current = setTimeout(() => {
        if (
          sidebarRef.current &&
          sidebarRef.current.matches(":hover") &&
          collapsed
        ) {
          setCollapsed(false)
          autoExpandedRef.current = true
        }
        hoverTimerRef.current = null
      }, 500)
    }
  }

  const handleSubmenuToggle = idx => {
    if (collapsed) {
      setCollapsed(false)
      setOpenSubmenu(idx)
    } else {
      setOpenSubmenu(openSubmenu === idx ? null : idx)
    }
  }

  const handleCollapseToggle = () => {
    clearHoverTimer()
    autoExpandedRef.current = false
    setCollapsed(prev => {
      if (!prev) setOpenSubmenu(null)
      return !prev
    })
  }

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        !collapsed &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        setCollapsed(true)
        setOpenSubmenu(null)
        autoExpandedRef.current = false
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [collapsed])

  useEffect(() => () => clearHoverTimer(), [])

  const showRequestDemo =
    user && subscriptionState && (isTrial || noSubscription)
  const bottomNavItems = [
    ...(showRequestDemo
      ? [
          {
            label: i18n.t("sidebar.requestDemo"),
            icon: <SlideshowIcon sx={{ fontSize: 20 }} />,
            buttonVariant: "contained",
            link: "https://meetings-eu1.hubspot.com/meetings/tizianopierini/trial-demo-",
            linkNewTab: true,
          },
          { separator: true },
        ]
      : []),
    // 26/01/2026 Comentado temporalmente hasta que volvamos a usarlo
    // {
    //   label: i18n.t("sidebar.changelog"),
    //   icon: <NotificationsIcon sx={{ fontSize: 20 }} />,
    //   buttonVariant: "text",
    //   dataAttribute: "data-featurebase-changelog",
    // },
    {
      label: i18n.t("sidebar.support"),
      icon: <HelpIcon sx={{ fontSize: 20 }} />,
      buttonVariant: "text",
      submenu: [
        {
          label: i18n.t("sidebar.whatsappSupport"),
          link: "https://api.whatsapp.com/send/?phone=34622235597&text=Hola%2C+%C2%BFen+que+puedo+ayudarte%3F&type=phone_number&app_absent=0",
          linkNewTab: true,
        },
        {
          label: i18n.t("sidebar.helpCenter"),
          link: "https://help.planhopper.com/es",
          linkNewTab: true,
        },
        {
          label: i18n.t("sidebar.feedback"),
          dataAttribute: "data-featurebase-feedback",
        },
      ],
    },
    {
      label: user ? user["custom:nombre_apellido"] : "",
      icon: <PersonIcon sx={{ fontSize: 20 }} />,
      buttonVariant: "text",
      tooltip: i18n.t("sidebar.account"),
      submenu: [
        {
          label: i18n.t("sidebar.profile"),
          link: "/profile",
        },
        {
          label: i18n.t("sidebar.companyManagement"),
          link: "/generalProfile",
        },
        {
          label: i18n.t("sidebar.subscription"),
          link: "/subscription",
        },
        {
          label: i18n.t("sidebar.logout"),
          onClick: () => {
            logOut()
          },
        },
      ],
    },
  ]

  const isActive = item => {
    if (item.activeRoutes) {
      return item.activeRoutes.some(route => {
        if (route.endsWith("/")) {
          return window.location.pathname.startsWith("/" + route)
        } else {
          return window.location.pathname === "/" + route
        }
      })
    }
    return window.location.pathname === item.link
  }

  return (
    <FeaturebaseWidget>
      <Box
        ref={sidebarRef}
        className="sidebar-root"
        onMouseEnter={handleMouseEnter}
        sx={{
          fontFamily: "Roboto, Arial, sans-serif",
          fontWeight: 400,
          left: 0,
          top: 0,
          width: collapsed ? 60 : 280,
          transition: "width 0.2s",
          bgcolor: "background.paper",
          height: "100vh",
          boxShadow: "0px 0 9px 0px #3B3B3B1A",
          display: "flex",
          flexDirection: "column",
          alignItems: collapsed ? "center" : "flex-start",
          position: "fixed",
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: collapsed ? "flex-start" : "flex-start",
            alignItems: "center",
            minHeight: 64,
          }}
        >
          <Box>
            <SidebarLogo
              collapsed={collapsed}
              style={{
                marginLeft: 13,
                cursor: "pointer",
              }}
            />
            <Box
              onClick={e => {
                e.stopPropagation()
                handleCollapseToggle()
              }}
              aria-label="Collapse sidebar"
              role="button"
              sx={{
                position: "absolute",
                right: -5,
                top: 17,
                width: 32,
                height: 32,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                opacity: collapsed ? 0 : 1,
                justifyContent: "center",
                borderRadius: "6px",
                backgroundColor: "white",
                boxShadow: "0 0 9px 0px #3B3B3B1A",
              }}
            >
              <ChevronLeftIcon />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            width: "100%",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
          }}
        >
          <List sx={{ width: "100%", gap: 0, p: 0 }}>
            {navItems.map((item, idx) => (
              <SidebarCentralNavButton
                key={idx}
                active={isActive(item)}
                item={item}
                collapsed={collapsed}
                openSubmenu={openSubmenu}
                idx={idx}
                handleSubmenuToggle={handleSubmenuToggle}
              />
            ))}
          </List>
        </Box>
        {/* Sección inferior */}
        <Box
          sx={{
            width: "100%",
            py: 2,
            px: collapsed ? 0 : 2,
            textAlign: collapsed ? "center" : "left",
            display: "flex",
            flexDirection: "column",
            alignItems: collapsed ? "center" : "flex-start",
            gap: 1,
          }}
        >
          {bottomNavItems.map((item, idx) =>
            item.separator ? (
              <Box
                key={"separator-" + idx}
                sx={{
                  width: collapsed ? "calc(100% - 10px)" : "100%",
                  borderTop: "1px solid #ccc",
                }}
              />
            ) : (
              <Tooltip
                key={idx}
                title={collapsed ? (item.tooltip ?? item.label) : ""}
                placement="right"
                disableInteractive={false}
              >
                <span style={{ width: "100%" }}>
                  <SidebarBottomNavButton
                    icon={item.icon}
                    label={item.label}
                    buttonVariant={item.buttonVariant}
                    onClick={item.onClick}
                    link={item.link}
                    linkNewTab={item.linkNewTab}
                    collapsed={collapsed}
                    dataAttribute={item.dataAttribute}
                    subItems={item.subItems || item.submenu}
                  />
                </span>
              </Tooltip>
            ),
          )}
        </Box>
      </Box>
    </FeaturebaseWidget>
  )
}

Sidebar.propTypes = {
  handleMiniClick: PropTypes.any,
  sidebarMini: PropTypes.bool,
}
export default Sidebar
